import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const InflationImpactCalculator = () => {
  const content = article.find((item) => item.id === 5);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [calculationType, setCalculationType] = useState("purchasing"); // purchasing, salary, investment

  const [inputs, setInputs] = useState({
    // Basic inputs
    currentAmount: "10000",
    inflationRate: "3",
    timePeriod: "10",
    
    // Salary adjustment
    currentSalary: "50000",
    requiredIncrease: "0",
    
    // Investment inputs
    investmentReturn: "8",
    investmentPeriod: "10",
    
    // Advanced inputs
    startingYear: new Date().getFullYear(),
    customInflation: "0",
    taxRate: "25",
    
    // Basket of goods
    housingCost: "1500",
    foodCost: "600",
    transportationCost: "400",
    healthcareCost: "300",
    entertainmentCost: "200",
    
    // Historical comparison
    historicalInflation: "3.2",
    comparisonYear: "2010",
    
    // Common inputs
    currency: "1",
    frequency: "monthly" // monthly, yearly
  });

  const [inflationScenarios, setInflationScenarios] = useState([
    { rate: "2", probability: "30", label: "Low Inflation" },
    { rate: "3", probability: "50", label: "Moderate Inflation" },
    { rate: "5", probability: "20", label: "High Inflation" }
  ]);

  const [expenseCategories, setExpenseCategories] = useState([
    { name: "Housing", amount: "1500", essential: true },
    { name: "Food", amount: "600", essential: true },
    { name: "Transportation", amount: "400", essential: true },
    { name: "Healthcare", amount: "300", essential: true },
    { name: "Entertainment", amount: "200", essential: false },
    { name: "Utilities", amount: "250", essential: true }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateInflationImpact();
    }
  }, [inputs, inflationScenarios, expenseCategories, calculationType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleScenarioChange = (index, field, value) => {
    const updatedScenarios = inflationScenarios.map((scenario, i) => 
      i === index ? { ...scenario, [field]: value } : scenario
    );
    setInflationScenarios(updatedScenarios);
  };

  const handleExpenseChange = (index, field, value) => {
    const updatedExpenses = expenseCategories.map((expense, i) => 
      i === index ? { ...expense, [field]: value } : expense
    );
    setExpenseCategories(updatedExpenses);
  };

  const addScenario = () => {
    setInflationScenarios([...inflationScenarios, { rate: "4", probability: "25", label: "Custom Scenario" }]);
  };

  const removeScenario = (index) => {
    if (inflationScenarios.length > 1) {
      setInflationScenarios(inflationScenarios.filter((_, i) => i !== index));
    }
  };

  const addExpense = () => {
    setExpenseCategories([...expenseCategories, { name: "New Category", amount: "100", essential: false }]);
  };

  const removeExpense = (index) => {
    if (expenseCategories.length > 1) {
      setExpenseCategories(expenseCategories.filter((_, i) => i !== index));
    }
  };

  const calculateInflationImpact = () => {
    const currencyRate = parseFloat(inputs.currency) || 1;
    const currentAmount = parseFloat(inputs.currentAmount) || 0;
    const inflationRate = parseFloat(inputs.inflationRate) || 0;
    const timePeriod = parseFloat(inputs.timePeriod) || 0;
    const currentSalary = parseFloat(inputs.currentSalary) || 0;
    const investmentReturn = parseFloat(inputs.investmentReturn) || 0;
    const investmentPeriod = parseFloat(inputs.investmentPeriod) || 0;
    const taxRate = parseFloat(inputs.taxRate) || 0;

    let futureAmount = 0;
    let purchasingPower = 0;
    let requiredSalary = 0;
    let realInvestmentReturn = 0;
    let calculationDetails = "";

    // Calculate based on calculation type
    switch (calculationType) {
      case "purchasing":
        // Future amount needed to maintain purchasing power
        futureAmount = currentAmount * Math.pow(1 + inflationRate/100, timePeriod);
        purchasingPower = currentAmount;
        calculationDetails = `Amount needed in ${timePeriod} years to match today's $${currentAmount} purchasing power`;
        break;

      case "salary":
        // Salary required to maintain purchasing power
        requiredSalary = currentSalary * Math.pow(1 + inflationRate/100, timePeriod);
        const salaryIncrease = ((requiredSalary / currentSalary - 1) * 100).toFixed(2);
        calculationDetails = `Salary needed in ${timePeriod} years: $${requiredSalary.toFixed(2)} (${salaryIncrease}% increase)`;
        break;

      case "investment":
        // Real return on investment after inflation
        const nominalFutureValue = currentAmount * Math.pow(1 + investmentReturn/100, investmentPeriod);
        const inflationAdjustedValue = nominalFutureValue / Math.pow(1 + inflationRate/100, investmentPeriod);
        realInvestmentReturn = ((inflationAdjustedValue / currentAmount - 1) * 100);
        futureAmount = inflationAdjustedValue;
        calculationDetails = `Real investment value after ${investmentPeriod} years considering inflation`;
        break;
    }

    // Calculate total expenses and future expenses
    const totalCurrentExpenses = expenseCategories.reduce((sum, expense) => 
      sum + parseFloat(expense.amount || 0), 0
    );
    
    const futureExpenses = expenseCategories.map(expense => ({
      ...expense,
      futureAmount: parseFloat(expense.amount || 0) * Math.pow(1 + inflationRate/100, timePeriod)
    }));

    const totalFutureExpenses = futureExpenses.reduce((sum, expense) => 
      sum + expense.futureAmount, 0
    );

    // Calculate essential vs discretionary expenses
    const essentialExpenses = futureExpenses.filter(expense => expense.essential);
    const discretionaryExpenses = futureExpenses.filter(expense => !expense.essential);
    
    const totalEssential = essentialExpenses.reduce((sum, expense) => sum + expense.futureAmount, 0);
    const totalDiscretionary = discretionaryExpenses.reduce((sum, expense) => sum + expense.futureAmount, 0);

    // Calculate inflation metrics
    const totalInflation = (Math.pow(1 + inflationRate/100, timePeriod) - 1) * 100;
    const annualInflationLoss = (inflationRate / 100) * currentAmount;
    const totalInflationLoss = futureAmount - currentAmount;

    // Calculate years to double (Rule of 72)
    const yearsToDouble = 72 / inflationRate;

    // Calculate real wage growth/decline
    const requiredAnnualIncrease = (Math.pow(requiredSalary / currentSalary, 1/timePeriod) - 1) * 100;
    const realWageChange = requiredAnnualIncrease - inflationRate;

    const resultData = {
      // Core calculations
      futureAmount: futureAmount * currencyRate,
      currentAmount: currentAmount * currencyRate,
      purchasingPower: purchasingPower * currencyRate,
      requiredSalary: requiredSalary * currencyRate,
      realInvestmentReturn,
      inflationRate,
      timePeriod,
      
      // Expense analysis
      totalCurrentExpenses: totalCurrentExpenses * currencyRate,
      totalFutureExpenses: totalFutureExpenses * currencyRate,
      futureExpenses,
      essentialExpenses: totalEssential * currencyRate,
      discretionaryExpenses: totalDiscretionary * currencyRate,
      expenseIncrease: ((totalFutureExpenses / totalCurrentExpenses - 1) * 100),
      
      // Inflation metrics
      totalInflation,
      annualInflationLoss: annualInflationLoss * currencyRate,
      totalInflationLoss: totalInflationLoss * currencyRate,
      yearsToDouble,
      
      // Salary analysis
      currentSalary: currentSalary * currencyRate,
      requiredAnnualIncrease,
      realWageChange,
      
      // Investment analysis
      investmentReturn,
      investmentPeriod,
      nominalReturn: investmentReturn,
      realReturn: realInvestmentReturn,
      
      // Additional details
      currencyRate,
      calculationType,
      calculationDetails,
      startingYear: parseInt(inputs.startingYear),
      endingYear: parseInt(inputs.startingYear) + timePeriod
    };

    setResult(resultData);
  };

  const calculateYearlyProjection = () => {
    if (!result) return [];

    const projection = [];
    const currentAmount = parseFloat(inputs.currentAmount) || 0;
    const inflationRate = parseFloat(inputs.inflationRate) || 0;
    
    for (let year = 0; year <= result.timePeriod; year++) {
      const futureValue = currentAmount * Math.pow(1 + inflationRate/100, year);
      const purchasingPower = currentAmount;
      const inflationLoss = futureValue - currentAmount;
      const cumulativeInflation = ((futureValue / currentAmount - 1) * 100);
      
      projection.push({
        year,
        futureValue,
        purchasingPower,
        inflationLoss,
        cumulativeInflation,
        realValue: currentAmount
      });
    }

    return projection;
  };

  const calculateScenarioAnalysis = () => {
    return inflationScenarios.map(scenario => {
      const rate = parseFloat(scenario.rate) || 0;
      const probability = parseFloat(scenario.probability) || 0;
      const currentAmount = parseFloat(inputs.currentAmount) || 0;
      const timePeriod = parseFloat(inputs.timePeriod) || 0;
      
      const futureAmount = currentAmount * Math.pow(1 + rate/100, timePeriod);
      const inflationLoss = futureAmount - currentAmount;
      const purchasingPowerLoss = ((currentAmount / futureAmount - 1) * 100);
      const expectedValue = (futureAmount * probability) / 100;
      
      return {
        ...scenario,
        futureAmount,
        inflationLoss,
        purchasingPowerLoss,
        expectedValue,
        yearsToDouble: 72 / rate
      };
    });
  };

  const calculateHistoricalComparison = () => {
    if (!result) return [];

    const currentYear = new Date().getFullYear();
    const comparisonYear = parseInt(inputs.comparisonYear) || 2010;
    const yearsDifference = currentYear - comparisonYear;
    const historicalInflation = parseFloat(inputs.historicalInflation) || 3.2;
    
    const historicalData = [];
    
    for (let year = comparisonYear; year <= currentYear; year++) {
      const yearsFromStart = year - comparisonYear;
      const valueThen = result.currentAmount / Math.pow(1 + historicalInflation/100, yearsDifference);
      const valueNow = result.currentAmount;
      const equivalentThen = valueNow / Math.pow(1 + historicalInflation/100, yearsFromStart);
      
      historicalData.push({
        year,
        valueThen: equivalentThen,
        valueNow,
        purchasingPower: (equivalentThen / valueNow) * 100
      });
    }

    return historicalData;
  };

  const calculateInflationImpactCategories = () => {
    if (!result) return [];

    return [
      { category: "Housing", current: parseFloat(inputs.housingCost) || 0, impact: 1.5 },
      { category: "Food", current: parseFloat(inputs.foodCost) || 0, impact: 1.3 },
      { category: "Transportation", current: parseFloat(inputs.transportationCost) || 0, impact: 1.4 },
      { category: "Healthcare", current: parseFloat(inputs.healthcareCost) || 0, impact: 1.7 },
      { category: "Entertainment", current: parseFloat(inputs.entertainmentCost) || 0, impact: 1.2 },
      { category: "Education", current: 200, impact: 1.6 }
    ].map(item => ({
      ...item,
      future: item.current * Math.pow(1 + (parseFloat(inputs.inflationRate) || 0)/100 * item.impact, result.timePeriod),
      increase: ((item.current * Math.pow(1 + (parseFloat(inputs.inflationRate) || 0)/100 * item.impact, result.timePeriod) / item.current - 1) * 100)
    }));
  };

  const resetCalculator = () => {
    setInflationScenarios([
      { rate: "2", probability: "30", label: "Low Inflation" },
      { rate: "3", probability: "50", label: "Moderate Inflation" },
      { rate: "5", probability: "20", label: "High Inflation" }
    ]);
    setExpenseCategories([
      { name: "Housing", amount: "1500", essential: true },
      { name: "Food", amount: "600", essential: true },
      { name: "Transportation", amount: "400", essential: true },
      { name: "Healthcare", amount: "300", essential: true },
      { name: "Entertainment", amount: "200", essential: false },
      { name: "Utilities", amount: "250", essential: true }
    ]);
    setInputs({
      currentAmount: "10000",
      inflationRate: "3",
      timePeriod: "10",
      currentSalary: "50000",
      requiredIncrease: "0",
      investmentReturn: "8",
      investmentPeriod: "10",
      startingYear: new Date().getFullYear(),
      customInflation: "0",
      taxRate: "25",
      housingCost: "1500",
      foodCost: "600",
      transportationCost: "400",
      healthcareCost: "300",
      entertainmentCost: "200",
      historicalInflation: "3.2",
      comparisonYear: "2010",
      currency: "1",
      frequency: "monthly"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Current Amount,${result.currentAmount.toFixed(2)}`,
      `Future Amount Needed,${result.futureAmount.toFixed(2)}`,
      `Inflation Rate,${result.inflationRate}%`,
      `Time Period,${result.timePeriod} years`,
      `Total Inflation,${result.totalInflation.toFixed(2)}%`,
      `Annual Inflation Loss,${result.annualInflationLoss.toFixed(2)}`,
      `Total Inflation Loss,${result.totalInflationLoss.toFixed(2)}`,
      `Years to Double,${result.yearsToDouble.toFixed(1)}`,
      `Current Expenses,${result.totalCurrentExpenses.toFixed(2)}`,
      `Future Expenses,${result.totalFutureExpenses.toFixed(2)}`,
      `Expense Increase,${result.expenseIncrease.toFixed(2)}%`,
      `Real Investment Return,${result.realInvestmentReturn.toFixed(2)}%`,
      `Required Salary Increase,${result.requiredAnnualIncrease.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inflation-impact.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      inflationScenarios: [...inflationScenarios],
      expenseCategories: [...expenseCategories],
      calculationType,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setInflationScenarios(calc.inflationScenarios || []);
    setExpenseCategories(calc.expenseCategories || []);
    setCalculationType(calc.calculationType || "purchasing");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        calculationType: "purchasing",
        inputs: { 
          currentAmount: "50000", 
          inflationRate: "2.5",
          timePeriod: "15"
        }
      },
      moderate: { 
        calculationType: "purchasing",
        inputs: { 
          currentAmount: "100000", 
          inflationRate: "3",
          timePeriod: "10"
        }
      },
      aggressive: { 
        calculationType: "purchasing",
        inputs: { 
          currentAmount: "250000", 
          inflationRate: "4",
          timePeriod: "20"
        }
      }
    };
    
    const presetData = presets[preset];
    setCalculationType(presetData.calculationType);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    addNotification(`${preset} inflation scenario applied`, "info");
  };

  // Chart Data
  const purchasingPowerChartData = result ? {
    labels: ["Current Purchasing Power", "Future Value Needed"],
    datasets: [{
      data: [result.currentAmount, result.futureAmount - result.currentAmount],
      backgroundColor: ["#19692C", "#FF6B6B"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const projectionData = calculateYearlyProjection();
  const projectionChartData = projectionData.length > 0 ? {
    labels: projectionData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Future Amount Needed',
        data: projectionData.map(d => d.futureValue),
        borderColor: "#FF6B6B",
        backgroundColor: "rgba(255, 107, 107, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Current Purchasing Power',
        data: projectionData.map(d => d.purchasingPower),
        borderColor: "#19692C",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  } : null;

  const scenarioData = calculateScenarioAnalysis();
  const scenarioChartData = scenarioData.length > 0 ? {
    labels: scenarioData.map(s => s.label),
    datasets: [
      {
        label: 'Future Amount Needed',
        data: scenarioData.map(s => s.futureAmount),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Inflation Loss',
        data: scenarioData.map(s => s.inflationLoss),
        backgroundColor: "#FF6B6B",
        borderColor: "#FF6B6B",
        borderWidth: 2
      }
    ]
  } : null;

  const expenseChartData = result ? {
    labels: result.futureExpenses.map(expense => expense.name),
    datasets: [
      {
        label: 'Current Amount',
        data: result.futureExpenses.map(expense => parseFloat(expense.amount)),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Future Amount',
        data: result.futureExpenses.map(expense => expense.futureAmount),
        backgroundColor: "#FF6B6B",
        borderColor: "#FF6B6B",
        borderWidth: 2
      }
    ]
  } : null;

  const historicalData = calculateHistoricalComparison();
  const historicalChartData = historicalData.length > 0 ? {
    labels: historicalData.map(d => d.year),
    datasets: [
      {
        label: 'Equivalent Past Value',
        data: historicalData.map(d => d.valueThen),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Current Value',
        data: historicalData.map(d => d.valueNow),
        borderColor: "#FF6B6B",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Inflation Impact Calculator</h1>
        </div>
        <p>Calculate purchasing power erosion, future expense requirements, and analyze inflation's impact on savings and investments</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit negative">
                        -${calc.totalInflationLoss?.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Inflation Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "expenses" ? "active" : ""}`}
                onClick={() => setActiveTab("expenses")}
              >
                <i className="fas fa-receipt"></i>
                {isMobile ? 'Expenses' : 'Expenses'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Inflation Scenarios:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Low (2.5%)
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate (3%)
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                High (4%)
              </button>
            </div>
          </div>

          {/* Calculation Type Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${calculationType === "purchasing" ? "active" : ""}`}
                onClick={() => setCalculationType("purchasing")}
              >
                <i className="fas fa-shopping-cart"></i>
                Purchasing Power
              </button>
              <button 
                className={`mode-btn ${calculationType === "salary" ? "active" : ""}`}
                onClick={() => setCalculationType("salary")}
              >
                <i className="fas fa-money-bill-wave"></i>
                Salary Impact
              </button>
              <button 
                className={`mode-btn ${calculationType === "investment" ? "active" : ""}`}
                onClick={() => setCalculationType("investment")}
              >
                <i className="fas fa-chart-line"></i>
                Investment Return
              </button>
            </div>
          </div>

          {/* Inflation Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Basic Inputs */}
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Basic Parameters
                  </h3>
                  <div className="input-row">
                    {calculationType === "purchasing" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Current Amount</label>
                        <input 
                          type="number" 
                          name="currentAmount" 
                          value={inputs.currentAmount} 
                          onChange={handleChange}
                          placeholder="10000"
                        />
                      </div>
                    )}
                    
                    {calculationType === "salary" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-money-bill-wave"></i> Current Salary</label>
                        <input 
                          type="number" 
                          name="currentSalary" 
                          value={inputs.currentSalary} 
                          onChange={handleChange}
                          placeholder="50000"
                        />
                      </div>
                    )}
                    
                    {calculationType === "investment" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-piggy-bank"></i> Investment Amount</label>
                        <input 
                          type="number" 
                          name="currentAmount" 
                          value={inputs.currentAmount} 
                          onChange={handleChange}
                          placeholder="10000"
                        />
                      </div>
                    )}
                    
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="3"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Time Period (Years)</label>
                      <input 
                        type="number" 
                        name="timePeriod" 
                        value={inputs.timePeriod} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                  </div>

                  {/* Investment Specific Inputs */}
                  {calculationType === "investment" && (
                    <>
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Investment Details
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-percentage"></i> Expected Return (%)</label>
                          <input 
                            type="number" 
                            name="investmentReturn" 
                            value={inputs.investmentReturn} 
                            onChange={handleChange}
                            placeholder="8"
                            step="0.1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-calendar"></i> Investment Period (Years)</label>
                          <input 
                            type="number" 
                            name="investmentPeriod" 
                            value={inputs.investmentPeriod} 
                            onChange={handleChange}
                            placeholder="10"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Starting Year</label>
                      <input 
                        type="number" 
                        name="startingYear" 
                        value={inputs.startingYear} 
                        onChange={handleChange}
                        placeholder="2024"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="taxRate" 
                        value={inputs.taxRate} 
                        onChange={handleChange}
                        placeholder="25"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Amount Frequency</label>
                      <select name="frequency" value={inputs.frequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>

                  {/* Historical Comparison */}
                  <h3>
                    <i className="fas fa-history"></i>
                    Historical Comparison
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Historical Inflation (%)</label>
                      <input 
                        type="number" 
                        name="historicalInflation" 
                        value={inputs.historicalInflation} 
                        onChange={handleChange}
                        placeholder="3.2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Comparison Year</label>
                      <input 
                        type="number" 
                        name="comparisonYear" 
                        value={inputs.comparisonYear} 
                        onChange={handleChange}
                        placeholder="2010"
                      />
                    </div>
                  </div>

                  {/* Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Expenses Tab */}
          {activeTab === "expenses" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-receipt"></i>
                  Expense Categories
                </h3>
                
                {expenseCategories.map((expense, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>Expense #{index + 1}</h4>
                      {expenseCategories.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeExpense(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Category Name</label>
                        <input 
                          type="text" 
                          value={expense.name}
                          onChange={(e) => handleExpenseChange(index, 'name', e.target.value)}
                          placeholder="Housing"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Monthly Amount</label>
                        <input 
                          type="number" 
                          value={expense.amount}
                          onChange={(e) => handleExpenseChange(index, 'amount', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-star"></i> Essential Expense</label>
                        <select 
                          value={expense.essential ? "true" : "false"}
                          onChange={(e) => handleExpenseChange(index, 'essential', e.target.value === "true")}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addExpense}>
                  <i className="fas fa-plus"></i>
                  Add Another Expense
                </button><br/>

                {/* Inflation Scenarios */}
                <h3>
                  <i className="fas fa-chess-board"></i>
                  Inflation Scenarios
                </h3>
                
                {inflationScenarios.map((scenario, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>{scenario.label}</h4>
                      {inflationScenarios.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeScenario(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Inflation Rate (%)</label>
                        <input 
                          type="number" 
                          value={scenario.rate}
                          onChange={(e) => handleScenarioChange(index, 'rate', e.target.value)}
                          placeholder="3"
                          step="0.1"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-chart-pie"></i> Probability (%)</label>
                        <input 
                          type="number" 
                          value={scenario.probability}
                          onChange={(e) => handleScenarioChange(index, 'probability', e.target.value)}
                          placeholder="50"
                          step="1"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Scenario Label</label>
                        <input 
                          type="text" 
                          value={scenario.label}
                          onChange={(e) => handleScenarioChange(index, 'label', e.target.value)}
                          placeholder="Scenario Name"
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addScenario}>
                  <i className="fas fa-plus"></i>
                  Add Another Scenario
                </button>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit negative">
                          <i className="fas fa-chart-line-down"></i>
                          -{calc.totalInflation?.toFixed(2)}%
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Loss: ${calc.totalInflationLoss?.toFixed(2)}</span>
                        <span><i className="fas fa-percentage"></i> Rate: {calc.inflationRate}%</span>
                        <span><i className="fas fa-calendar"></i> Years: {calc.timePeriod}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "expenses") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line-down"></i>
                    Inflation Impact Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight negative">
                        <div className="result-value negative">
                          ${result.futureAmount.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Future Amount Needed
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.currentAmount.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-dollar-sign"></i>
                          Current Amount
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value negative">
                          ${result.totalInflationLoss.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Total Inflation Loss
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.totalInflation.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Total Inflation
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Annual Inflation Rate:</span>
                        <span>{result.inflationRate}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Time Period:</span>
                        <span>{result.timePeriod} years ({result.startingYear} - {result.endingYear})</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-clock"></i> Years to Double Prices:</span>
                        <span>{result.yearsToDouble.toFixed(1)} years</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shopping-cart"></i> Purchasing Power Loss:</span>
                        <span className="negative">-{result.totalInflation.toFixed(2)}%</span>
                      </div>
                      {calculationType === "salary" && (
                        <>
                          <div className="detail-row">
                            <span><i className="fas fa-money-bill-wave"></i> Required Salary Increase:</span>
                            <span>{result.requiredAnnualIncrease.toFixed(2)}% annually</span>
                          </div>
                          <div className="detail-row">
                            <span><i className="fas fa-chart-line"></i> Real Wage Change:</span>
                            <span className={result.realWageChange >= 0 ? 'positive' : 'negative'}>
                              {result.realWageChange >= 0 ? '+' : ''}{result.realWageChange.toFixed(2)}%
                            </span>
                          </div>
                        </>
                      )}
                      {calculationType === "investment" && (
                        <>
                          <div className="detail-row">
                            <span><i className="fas fa-chart-line"></i> Nominal Return:</span>
                            <span>{result.nominalReturn}%</span>
                          </div>
                          <div className="detail-row">
                            <span><i className="fas fa-chart-line-down"></i> Real Return:</span>
                            <span className={result.realReturn >= 0 ? 'positive' : 'negative'}>
                              {result.realReturn.toFixed(2)}%
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Key Metrics */}
                    <div className="risk-summary">
                      <h4>Inflation Impact Metrics</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.annualInflationLoss.toFixed(2)}
                          </div>
                          <div className="risk-label">Annual Loss</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.yearsToDouble.toFixed(1)} yrs
                          </div>
                          <div className="risk-label">Double Time</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value negative">
                            -{result.totalInflation.toFixed(2)}%
                          </div>
                          <div className="risk-label">Power Loss</div>
                        </div>
                      </div>
                    </div>

                    {/* Purchasing Power Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Purchasing Power Erosion
                      </h3>
                      <Doughnut 
                        data={purchasingPowerChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Projection Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Inflation Impact Over Time
                      </h3>
                      <Line 
                        data={projectionChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Historical Comparison Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-history"></i>
                        Historical Comparison
                      </h3>
                      <Line 
                        data={historicalChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Yearly Projection Table */}
                    <div className="risk-summary">
                      <h4>Yearly Projection</h4>
                      <div className="projection-table">
                        <div className="table-header">
                          <span>Year</span>
                          <span>Amount Needed</span>
                          <span>Inflation Loss</span>
                          <span>Cumulative Inflation</span>
                        </div>
                        {projectionData.filter((_, index) => index % Math.ceil(projectionData.length / 8) === 0 || index === projectionData.length - 1).map((year, index) => (
                          <div key={index} className="table-row">
                            <span>{year.year}</span>
                            <span>${year.futureValue.toFixed(2)}</span>
                            <span className="negative">${year.inflationLoss.toFixed(2)}</span>
                            <span className="negative">{year.cumulativeInflation.toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-chart-line-down fa-3x"></i>
                    <p>Enter your details to calculate inflation impact</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "expenses" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-receipt"></i>
                  Expense Impact Analysis
                </h3>
                
                <div className="expenses-summary">
                  <div className="summary-card">
                    <div className="summary-value">${result.totalCurrentExpenses.toFixed(2)}</div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Current Monthly Expenses
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">${result.totalFutureExpenses.toFixed(2)}</div>
                    <div className="summary-label">
                      <i className="fas fa-bullseye"></i>
                      Future Monthly Expenses
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value negative">+{result.expenseIncrease.toFixed(2)}%</div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line-up"></i>
                      Total Increase
                    </div>
                  </div><br/>
                </div>

                {/* Expense Comparison Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Expense Category Impact
                  </h3>
                  <Bar 
                    data={expenseChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Scenario Analysis Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chess-board"></i>
                    Multi-Scenario Analysis
                  </h3>
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Detailed Expense Breakdown */}
                <div className="expenses-details">
                  <h4>Expense Category Details</h4>
                  <div className="expenses-list">
                    {result.futureExpenses.map((expense, index) => (
                      <div key={index} className="expense-item">
                        <div className="expense-header">
                          <span className="expense-name">
                            {expense.essential && <i className="fas fa-star essential-icon"></i>}
                            {expense.name}
                          </span>
                          <span className="expense-increase negative">
                            +{((expense.futureAmount / parseFloat(expense.amount) - 1) * 100).toFixed(2)}%
                          </span>
                        </div>
                        <div className="expense-details">
                          <div className="expense-metric">
                            <span>Current:</span>
                            <span>${parseFloat(expense.amount).toFixed(2)}</span>
                          </div>
                          <div className="expense-metric">
                            <span>Future:</span>
                            <span>${expense.futureAmount.toFixed(2)}</span>
                          </div>
                          <div className="expense-metric">
                            <span>Increase:</span>
                            <span className="negative">${(expense.futureAmount - parseFloat(expense.amount)).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div><br/>
                </div>

                {/* Scenario Analysis */}
                <div className="scenario-analysis">
                  <h4>Inflation Scenario Analysis</h4><br/>
                  <div className="scenario-list">
                    {scenarioData.map((scenario, index) => (
                      <div key={index} className="scenario-item">
                        <div className="scenario-header">
                          <span className="scenario-name">{scenario.label}</span>
                          <span className="scenario-rate">{scenario.rate}% Inflation</span>
                        </div>
                        <div className="scenario-details">
                          <div className="scenario-metric">
                            <span>Future Amount Needed:</span>
                            <span>${scenario.futureAmount.toFixed(2)}</span>
                          </div>
                          <div className="scenario-metric">
                            <span>Inflation Loss:</span>
                            <span className="negative">${scenario.inflationLoss.toFixed(2)}</span>
                          </div>
                          <div className="scenario-metric">
                            <span>Purchasing Power Loss:</span>
                            <span className="negative">{scenario.purchasingPowerLoss.toFixed(2)}%</span>
                          </div>
                          <div className="scenario-metric">
                            <span>Years to Double:</span>
                            <span>{scenario.yearsToDouble.toFixed(1)} years</span>
                          </div>
                          <div className="scenario-metric">
                            <span>Probability:</span>
                            <span>{scenario.probability}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div><br/>
                </div>

                <div className="inflation-tips">
                  <h4>Inflation Protection Tips</h4><br/>
                  <div className="tips-list">
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Invest in assets that outpace inflation (stocks, real estate)</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Consider Treasury Inflation-Protected Securities (TIPS)</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Negotiate salary increases that match or exceed inflation</span>
                    </div>
                    <div className="tip-item warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Cash savings lose purchasing power during high inflation periods</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Diversify investments across different asset classes and geographies</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default InflationImpactCalculator;